import { IStringModule } from "./IStringModule";
import { StringObject } from "../objects/StringObject";
import { StringToolsContainer } from "./StringToolsContainer";
import { StringData } from "./StringData";
import { AttributeType } from "../models/attribute/AttributeType";
import { StringContext } from "./StringContext";
import { EventManager } from "./managers/EventManager";
import { AttributeMapping } from "../models/attribute/AttributeMapping";

/**
 * Base class for a module used in the string-tune system.
 * Extend this class to create custom modules that respond to scroll, resize, input, etc.
 */
export class StringModule implements IStringModule {
  /**
   * List of attribute names this module should automatically read
   * from the DOM element and assign to the object properties.
   * Example: ["offset-top", "offset-bottom"]
   */
  protected attributesToMap: AttributeMapping[];

  /**
   * A map that associates string keys with `StringObject` instances.
   * This map is used to manage and track `StringObject` instances on a page.
   */
  protected objectMapOnPage: Map<string, StringObject> = new Map();

  /**
   * A protected array that holds the collection of `StringObject` instances
   * currently present on the page.
   */
  protected objectsOnPage: StringObject[] = [];

  /**
   * A map of all entered objects by their unique ID.
   */
  protected objectMap: Map<string, StringObject> = new Map();

  /**
   * A flat array of all connected objects.
   */
  protected objects: StringObject[] = [];

  /**
   * The HTML attribute key that identifies objects this module is responsible for.
   */
  protected htmlKey: string = "";

  /**
   * Module type ID used internally to categorize module behavior.
   */
  protected _type: number = 1;

  /**
   * Returns the type of the module.
   * Type 1 = core module, type 2 = UI module.
   */
  public get type(): number {
    return this._type;
  }

  /**
   * Tools container providing utilities for attribute parsing, unit conversion, etc.
   * Acts as a dependency injection hub for core IStringTool implementations.
   */
  protected tools: StringToolsContainer;

  /**
   * Shared global data object containing scroll state, viewport info, cursor position, etc.
   * Used for calculations within lifecycle hooks like `onScroll`, `onFrame`, and `onResize`.
   */
  protected data: StringData;

  /**
   * Configuration object specific to the current module.
   * Passed in during module registration or initialization.
   */
  protected settings: Record<string, any>;

  /**
   * Event hub for communication between modules or systems.
   * Supports custom event emitting, listening, and unsubscription.
   */
  protected events: EventManager;

  constructor(context: StringContext) {
    this.tools = context.tools;
    this.data = context.data;
    this.settings = context.settings;
    this.events = context.events;

    this.attributesToMap = [
      { key: "active", type: "boolean", fallback: this.settings["active"] },
      { key: "fixed", type: "boolean", fallback: this.settings["fixed"] },
      { key: "repeat", type: "boolean", fallback: this.settings["repeat"] },
      {
        key: "self-disable",
        type: "boolean",
        fallback: this.settings["self-disable"],
      },
      { key: "abs", type: "boolean", fallback: this.settings["abs"] },
      { key: "key", type: "string", fallback: this.settings["key"] },
      {
        key: "offset-top",
        type: "dimension",
        fallback: this.settings["offset-top"],
      },
      {
        key: "offset-bottom",
        type: "dimension",
        fallback: this.settings["offset-bottom"],
      },
      {
        key: "inview-top",
        type: "dimension",
        fallback: this.settings["inview-top"],
      },
      {
        key: "inview-bottom",
        type: "dimension",
        fallback: this.settings["inview-bottom"],
      },
      {
        key: "start",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          const top = boundingRect.top;
          return (
            Math.floor(top) +
            this.data.scroll.container.scrollTop * this.data.viewport.transformScale
          );
        },
      },
      {
        key: "end",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          const top = boundingRect.top;
          const height = boundingRect.height;
          return top + height - this.data.scroll.transformedCurrent;
        },
      },
      {
        key: "size",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          return boundingRect.height;
        },
      },
      {
        key: "half-width",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          return boundingRect.width / 2;
        },
      },
      {
        key: "half-height",
        type: "number",
        fallback: (element: HTMLElement, object: StringObject, boundingRect: DOMRect) => {
          return boundingRect.height / 2;
        },
      },
      { key: "enter-el", type: "string", fallback: this.settings["enter-el"] },
      { key: "enter-vp", type: "string", fallback: this.settings["enter-vp"] },
      { key: "exit-el", type: "string", fallback: this.settings["exit-el"] },
      { key: "exit-vp", type: "string", fallback: this.settings["exit-vp"] },
    ];
  }

  /**
   * Initializes a `StringObject` by mapping attributes from an HTML element
   * and applying transformations as needed.
   *
   * @param globalId - A unique identifier for the object being initialized.
   * @param object - The `StringObject` instance to be initialized.
   * @param element - The HTML element from which attributes are extracted.
   * @param attributes - A record of additional attributes to be used during initialization.
   *
   * The method performs the following steps:
   * 1. Retrieves the bounding rectangle of the provided HTML element.
   * 2. Iterates over a predefined list of attributes to map.
   * 3. Resolves fallback values for attributes, either from the provided attributes,
   *    settings, or a fallback function.
   * 4. Processes and parses the raw attribute values based on their expected type.
   * 5. Applies optional transformations to the parsed values.
   * 6. Sets the processed attributes as properties on the `StringObject` instance.
   */
  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    let boundingRect = this.tools.boundingClientRect.process({ element });
    for (const { key, type, fallback, transform } of this.attributesToMap) {
      const resolvedFallback =
        typeof fallback === "function" ? fallback(element, object, boundingRect) : fallback;
      const raw = this.tools.domAttribute.process({
        element,
        key,
        fallback: attributes[key] ?? this.settings[key] ?? resolvedFallback,
      });

      let parsed = this.parseAttribute(raw, type, {
        element,
        boundingRect,
        viewportHeight: this.data.viewport.windowHeight,
        baseRem: this.data.viewport.baseRem,
      });

      if (transform) {
        parsed = transform(parsed);
      }
      object.setProperty(key, parsed);
    }
  }

  /**
   * Calculates object-specific positions or metrics based on the current layout or scroll state.
   * This method is intended to be overridden in subclasses if the module needs to precompute
   * layout-dependent values (e.g. parallax offsets, trigger zones, distances).
   *
   * @param object The `StringObject` instance whose positions are being calculated.
   * @param windowSize The current window height or width (depending on scroll axis).
   */
  calculatePositions(object: StringObject, windowSize: number) {
    const start = object.getProperty<number>("start");
    const size = object.getProperty<number>("size");

    const offsetStart = object.getProperty<number>("offset-bottom");
    const offsetEnd = object.getProperty<number>("offset-top");

    const startElement = object.getProperty("enter-el");
    const startViewport = object.getProperty("enter-vp");
    const endElement = object.getProperty("exit-el");
    const endViewport = object.getProperty("exit-vp");

    let startPosition = 0;
    let endPosition = 0;
    let startBias = 0;
    let endBias = 0;

    if (
      (startElement === "top" && startViewport === "top") ||
      (startElement === "left" && startViewport === "left")
    ) {
      startBias = -windowSize + 1;
      startPosition = start - offsetStart;
    } else if (
      (startElement === "top" && startViewport === "bottom") ||
      (startElement === "left" && startViewport === "right")
    ) {
      startPosition = start - windowSize - offsetStart;
    } else if (
      (startElement === "bottom" && startViewport === "top") ||
      (startElement === "right" && startViewport === "left")
    ) {
      startBias = -windowSize - size + 1;
      startPosition = start + size - offsetStart;
    } else if (
      (startElement === "bottom" && startViewport === "bottom") ||
      (startElement === "right" && startViewport === "right")
    ) {
      startBias = -size + 1;
      startPosition = start - windowSize + size - offsetStart;
    }

    // End position calculation
    if (
      (endElement === "top" && endViewport === "top") ||
      (endElement === "left" && endViewport === "left")
    ) {
      endBias = -size + 1;
      endPosition = start + offsetEnd;
    } else if (
      (endElement === "top" && endViewport === "bottom") ||
      (endElement === "left" && endViewport === "right")
    ) {
      endBias = -windowSize - size + 1;
      endPosition = start - windowSize + offsetEnd;
    } else if (
      (endElement === "bottom" && endViewport === "top") ||
      (endElement === "right" && endViewport === "left")
    ) {
      endPosition = start + size + offsetEnd;
    } else if (
      (endElement === "bottom" && endViewport === "bottom") ||
      (endElement === "right" && endViewport === "right")
    ) {
      endBias = -windowSize + 1;
      endPosition = start - windowSize + size + offsetEnd;
    }

    object.setProperty<number>("start-bias", startBias);
    object.setProperty<number>("end-bias", endBias);

    object.setProperty<number>("start-position", startPosition - this.data.scroll.topPosition);
    object.setProperty<number>("end-position", endPosition - this.data.scroll.topPosition);
    object.setProperty<number>("difference-position", endPosition - startPosition);

    const inviewTop = object.getProperty<number>("inview-top") ?? 0;
    const inviewBottom = object.getProperty<number>("inview-bottom") ?? 0;
    object.setProperty<number>(
      "inview-start-position",
      object.getProperty<number>("start-position") + inviewTop
    );
    object.setProperty<number>(
      "inview-end-position",
      object.getProperty<number>("end-position") + inviewBottom
    );
  }

  /**
   * Parses a raw DOM attribute value into the correct type based on mapping.
   * Handles fallback resolution, transformation, and special units.
   *
   * @param value Raw attribute string from DOM.
   * @param type The expected attribute type (e.g. number, boolean, dimension).
   * @param context Optional helper values like element or viewport size.
   * @returns The parsed and transformed value.
   */
  protected parseAttribute(
    value: string | null,
    type: AttributeType,
    context: {
      element?: HTMLElement;
      boundingRect?: DOMRect;
      viewportHeight?: number;
      baseRem?: number;
    } = {}
  ): any {
    if (value == null) return null;

    if (typeof type === "object" && type.type === "enum") {
      return type.values.includes(value) ? value : type.values[0];
    }

    switch (type) {
      case "number":
        return parseFloat(value);

      case "boolean":
        return value === "" || value === "true";

      case "json":
        try {
          return JSON.parse(value);
        } catch {
          return null;
        }

      case "tuple":
        return value.trim().split(/\s+/);

      case "easing":
        return this.tools.easingFunction.process({ easing: value });

      case "color":
        return this.tools.colorParser.process({ value: value });

      case "dimension":
        if (value == "0") return 0;
        if (
          context.element != null &&
          context.viewportHeight != null &&
          context.baseRem != null &&
          context.boundingRect != null
        ) {
          return this.tools.unitParser.process({
            value,
            element: context.element,
            viewportHeight: context.viewportHeight,
            boundingRect: context.boundingRect,
            baseRem: context.baseRem,
          });
        } else {
          return 0;
        }

      default:
        return value;
    }
  }

  /**
   * Determines whether the module should attach to a given object,
   * based on the presence of the module's `htmlKey` in the object keys.
   *
   * @param object The target object to test.
   * @returns `true` if the module can connect, `false` otherwise.
   */
  canConnect(object: StringObject): boolean {
    return object.keys.includes(this.htmlKey);
  }

  /**
   * Registers the module on a given object, adds the object to internal list,
   * and triggers connection logic.
   *
   * @param object The object to connect to.
   */
  connectObject(object: StringObject): void {
    object.connect(this);
    this.onObjectConnected(object);
  }

  /**
   * Registers the object internally when it enters the module’s scope.
   */
  enterObject(id: string, object: StringObject): void {
    if (!this.objectMap.has(id)) {
      this.objectMap.set(id, object);
      this.objects.push(object);
    }
  }

  /**
   * Unregisters the object when it leaves the module’s scope.
   */
  exitObject(id: string): void {
    const object = this.objectMap.get(id);
    if (!object) return;

    this.objectMap.delete(id);

    const index = this.objects.indexOf(object);
    if (index !== -1) {
      this.objects.splice(index, 1);
    }
  }

  /**
   * Adds a `StringObject` to the internal collections if it does not already exist.
   *
   * @param id - The unique identifier for the `StringObject`.
   * @param object - The `StringObject` to be added.
   *
   * @remarks
   * This method ensures that the object is added to both `objectMapOnPage` and
   * `objectsOnPage` only if the `id` is not already present in `objectMapOnPage`.
   */
  addObject(id: string, object: StringObject): void {
    if (!this.objectMapOnPage.has(id)) {
      this.objectMapOnPage.set(id, object);
      this.objectsOnPage.push(object);
    }
  }

  /**
   * Removes an object from the page by its unique identifier.
   *
   * This method performs the following steps:
   * 1. Retrieves the object associated with the given `id` from `objectMapOnPage`.
   * 2. If the object is not found, the method exits early.
   * 3. Deletes the object from `objectMapOnPage`.
   * 4. Finds the index of the object in the `objectsOnPage` array.
   * 5. If the object exists in the array, it is removed using `splice`.
   *
   * @param id - The unique identifier of the object to be removed.
   */
  removeObject(id: string): void {
    const object = this.objectMapOnPage.get(id);
    if (!object) return;

    this.objectMapOnPage.delete(id);

    const index = this.objectsOnPage.indexOf(object);
    if (index !== -1) {
      this.objectsOnPage.splice(index, 1);
    }
  }

  /**
   * Called when an object is connected. Can be overridden to apply initial styles or logic.
   * @param object The connected object.
   */
  onObjectConnected(object: StringObject): void {}

  /**
   * Applies a style or callback to both the main element and all its connected elements.
   *
   * @param object The object whose elements to update.
   * @param applyFn The function that receives an HTMLElement and performs any update.
   */
  protected applyToElementAndConnects(object: StringObject, applyFn: (el: HTMLElement) => void) {
    if (object.getProperty("self-disable") == false) {
      applyFn(object.htmlElement);
    }
    // console.log(object.connects);
    object.connects.forEach(applyFn);
  }

  /**
   * Cleans up internal state and detaches the module from the system.
   */
  destroy(): void {
    this.objects = [];
    this.objectMap = new Map();
  }

  // ───────────────────────────────
  // Lifecycle Hooks
  // ───────────────────────────────

  /** Called once when the module is initialized. */
  onInit(): void {}

  /** Called on each frame with current scroll and state data. */
  onFrame(data: StringData): void {}

  /** Called when the window or layout is resized. */
  onResize(): void {}

  /** Called when scroll position changes. */
  onScroll(data: StringData): void {}

  /** Called when user changed scroll diraction. */
  onDirectionChange(): void {}

  /** Called when user starts scrolling. */
  onScrollStart(): void {}

  /** Called when user stops scrolling. */
  onScrollStop(): void {}

  /** Called when scroll direction changes (e.g., up ↔ down). */
  onScrollDirectionChange(): void {}

  /** Called when scroll axis changes (vertical ↔ horizontal). */
  onAxisChange(): void {}

  /** Called when device type changes (e.g., desktop ↔ mobile). */
  onDeviceChange(): void {}

  /** Called when scroll-related system settings or parameters change. */
  onScrollConfigChange(): void {}

  /** Called when scroll-related system settings or parameters change. */
  onSettingsChange(): void {}

  /** Called when the DOM is rebuilt, such as after a major mutation. */
  onDOMRebuild(): void {}

  /** Called on every mouse movement. */
  onMouseMove(event: MouseEvent): void {}

  /** Called on wheel input (independent of scroll). */
  onWheel(event: WheelEvent): void {}

  /**
   * Called when DOM elements are added or removed.
   */
  onDOMMutate(added: NodeList, removed: NodeList): void {}
}
